import React, { useEffect } from 'react';
import { View, Text, StyleSheet, Animated, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../store';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { HIDE_NOTIFICATION } from '../store/constants';
import type { RootState } from '../store';
import { colors } from '../theme';
import { isWeb, DESKTOP_BREAKPOINT, WIDGET_GUTTER } from '../utilities/platform';

const typeColors: Record<string, string> = {
  success: colors.success,
  error: colors.error,
  warning: colors.warning,
  info: colors.electricBlue,
};

// Toast geometry on desktop. The widget shell maxes at 1400 with the shared
// WIDGET_GUTTER either side (utilities/platform — the single source of truth
// DesktopLayout and the update banners also import). The toast anchors to the
// top of that frame (so it never collides with the chrome), but its own width
// is capped much tighter — toasts are conversational UI carrying ≤ 2 lines of
// text, and stretching them across the full widget body produces a
// system-banner feel that dwarfs the content.
//
// 520px matches the "narrow content" cap used elsewhere in the app and
// roughly mirrors macOS / Slack / Discord native notifications. On phones
// the toast collapses to viewport-padding (left/right: 16) as before.
const DESKTOP_TOAST_MAX_WIDTH = 520;

export const Notification = () => {
  const dispatch = useAppDispatch();
  const notification = useSelector((state: RootState) => state.notification);
  const opacity = React.useRef(new Animated.Value(0)).current;
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktopWidth = isWeb && width >= DESKTOP_BREAKPOINT;

  useEffect(() => {
    if (notification.visible) {
      Animated.timing(opacity, { toValue: 1, duration: 250, useNativeDriver: true }).start();
      const timer = setTimeout(() => dispatch({ type: HIDE_NOTIFICATION }), 4000);
      return () => clearTimeout(timer);
    } else {
      Animated.timing(opacity, { toValue: 0, duration: 200, useNativeDriver: true }).start();
    }
  }, [notification.visible]);

  if (!notification.visible) return null;

  const bgColor = typeColors[notification.type] || colors.electricBlue;

  // On desktop the toast is centered horizontally and capped at a tight
  // toast width (not the widget envelope). `left/right: 0` + `margin: auto`
  // is a CSS-only centering trick that RN-Web honours for absolutely-
  // positioned elements without fighting the Animated opacity transform on
  // the same node (which `transform: translateX(-50%)` would).
  //
  // Vertical: the widget itself starts WIDGET_GUTTER down from the viewport
  // top, so we offset the toast by gutter + 8 to land just inside the
  // widget's top edge instead of floating above it.
  const topOffset = isDesktopWidth
    ? WIDGET_GUTTER + 8
    : insets.top + 8;
  const desktopOverrides = isDesktopWidth
    ? {
        left: 0,
        right: 0,
        marginLeft: 'auto' as unknown as number,
        marginRight: 'auto' as unknown as number,
        // Cap at toast width OR viewport-32 (with 16px gutters), whichever
        // is smaller, so very narrow desktop windows still get sane padding.
        width: Math.min(DESKTOP_TOAST_MAX_WIDTH, width - 32),
        maxWidth: DESKTOP_TOAST_MAX_WIDTH,
      }
    : null;

  return (
    <Animated.View
      style={[
        styles.container,
        { opacity, backgroundColor: bgColor, top: topOffset },
        desktopOverrides,
      ]}
    >
      <View style={styles.content}>
        {!!notification.title && <Text style={styles.title}>{notification.title}</Text>}
        {!!notification.message && <Text style={styles.message}>{notification.message}</Text>}
      </View>
      <TouchableOpacity onPress={() => dispatch({ type: HIDE_NOTIFICATION })} style={styles.dismiss}>
        <Text style={styles.dismissText}>✕</Text>
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: 16,
    right: 16,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
    zIndex: 999,
  },
  content: {
    flex: 1,
  },
  title: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
    marginBottom: 2,
  },
  message: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 13,
  },
  dismiss: {
    marginLeft: 12,
    padding: 4,
  },
  dismissText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 14,
    fontWeight: '600',
  },
});
